import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "لوتکا";
const DEFAULT_TITLE = "لوتکا | راهنمای سفر به گیلان";
const DEFAULT_DESCRIPTION = "لوتکا راهنمای سفر به گیلان برای کشف جاذبه‌های دیدنی، رستوران‌ها، کافه‌ها و اقامتگاه‌ها است.";

const decodeRoutePart = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getPageMetadata = (pathname: string) => {
  if (pathname === "/") {
    return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
  }

  if (pathname === "/destinations") {
    return {
      title: "شهرهای گیلان | لوتکا",
      description: "راهنمای سفر و معرفی شهرهای گیلان؛ جاذبه‌ها، رستوران‌ها، کافه‌ها و اقامتگاه‌های هر شهر را در لوتکا ببینید.",
    };
  }

  if (pathname.startsWith("/places/city/")) {
    const cityName = decodeRoutePart(pathname.replace("/places/city/", ""));
    return {
      title: `راهنمای سفر به ${cityName} | لوتکا`,
      description: `جاهای دیدنی، رستوران‌ها، کافه‌ها و اقامتگاه‌های ${cityName} را در لوتکا کشف کنید.`,
    };
  }

  if (pathname.startsWith("/places/category/")) {
    const category = decodeRoutePart(pathname.replace("/places/category/", ""));
    return {
      title: `${category}‌های گیلان | لوتکا`,
      description: `بهترین ${category}‌های گیلان را همراه با امتیاز، نظر کاربران و اطلاعات کاربردی در لوتکا ببینید.`,
    };
  }

  if (pathname.startsWith("/places/")) {
    return {
      title: "اطلاعات مکان‌های دیدنی گیلان | لوتکا",
      description: "جزئیات مکان، امتیاز کاربران، تصاویر، مسیر دسترسی و اطلاعات کاربردی مکان‌های گیلان در لوتکا.",
    };
  }

  if (pathname === "/reviews") {
    return {
      title: "نظرات کاربران درباره مکان‌های گیلان | لوتکا",
      description: "تجربه و دیدگاه کاربران درباره جاذبه‌ها، رستوران‌ها، کافه‌ها و اقامتگاه‌های گیلان.",
    };
  }

  if (pathname === "/planner") {
    return {
      title: "برنامه‌ریز سفر گیلان | لوتکا",
      description: "برای سفر خود به گیلان برنامه‌ای متناسب با شهر، زمان و سلیقه‌تان در لوتکا بسازید.",
    };
  }

  if (pathname === "/about") {
    return {
      title: "درباره لوتکا | پلتفرم بومی سفر به گیلان",
      description: "با لوتکا، پلتفرم بومی کشف تجربه‌های سفر در گیلان، بیشتر آشنا شوید.",
    };
  }

  return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
};

const setMeta = (selector: string, attribute: "name" | "property", key: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
};

const SeoMetadata = () => {
  const location = useLocation();

  useEffect(() => {
    const { title, description } = getPageMetadata(location.pathname);
    const siteUrl = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");
    const canonicalUrl = `${siteUrl}${location.pathname}`;
    const imageUrl = `${siteUrl}/lootka-logo.png`;

    document.title = title;
    document.documentElement.lang = "fa";
    document.documentElement.dir = "rtl";

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="robots"]', "name", "robots", "index,follow,max-image-preview:large");
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    setMeta('meta[property="og:locale"]', "property", "og:locale", "fa_IR");
    setMeta('meta[property="og:image"]', "property", "og:image", imageUrl);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", imageUrl);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    let structuredData = document.head.querySelector<HTMLScriptElement>("#lootka-website-schema");
    if (!structuredData) {
      structuredData = document.createElement("script");
      structuredData.id = "lootka-website-schema";
      structuredData.type = "application/ld+json";
      document.head.appendChild(structuredData);
    }
    structuredData.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: siteUrl,
      inLanguage: "fa-IR",
      description: DEFAULT_DESCRIPTION,
    });
  }, [location.pathname]);

  return null;
};

export default SeoMetadata;
